/**
 * Admin API helper — использует admin_token из localStorage.
 *
 * ВАЖНО: нельзя использовать api.* из './api' — тот читает
 * localStorage.getItem('token') (пользовательский JWT),
 * тогда как админка хранит JWT как localStorage.getItem('admin_token').
 */

const ADMIN_BASE = '/api/admin';

export async function adminFetch<T>(method: string, url: string, body?: unknown): Promise<T> {
  const token = localStorage.getItem('admin_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fullUrl = url.startsWith('http') ? url : url; // all admin URLs relative
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.message || errData.error || `HTTP ${res.status}`);
  }

  const json = await res.json();
  // Unwrap { success, data } envelope
  return (json.data ?? json) as T;
}

// ============ Auth ============

export interface AdminLoginResponse {
  admin: {
    id: string;
    email: string;
    full_name: string;
    role: string;
    totp_enabled: boolean;
  };
  token: string;
  refreshToken: string;
  requires2FA: boolean;
}

export function adminLogin(email: string, password: string) {
  return adminFetch<AdminLoginResponse>('POST', `${ADMIN_BASE}/auth/login`, { email, password });
}

export function adminVerify2FA(email: string, password: string, code: string) {
  return adminFetch<AdminLoginResponse>('POST', `${ADMIN_BASE}/auth/verify-2fa`, { email, password, code });
}

export interface TwoFactorSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

export function admin2FASetup() {
  return adminFetch<TwoFactorSetupResponse>('POST', `${ADMIN_BASE}/auth/2fa/setup`);
}

export function admin2FAEnable(code: string) {
  return adminFetch<void>('POST', `${ADMIN_BASE}/auth/2fa/enable`, { code });
}

export function admin2FADisable(code: string) {
  return adminFetch<void>('POST', `${ADMIN_BASE}/auth/2fa/disable`, { code });
}

export function adminRefresh(refreshToken: string) {
  return adminFetch<{ token: string; refreshToken: string }>('POST', `${ADMIN_BASE}/auth/refresh`, {
    refreshToken,
  });
}

export function adminLogout(refreshToken?: string) {
  return adminFetch<void>('POST', `${ADMIN_BASE}/auth/logout`, { refreshToken });
}

export function adminMe() {
  return adminFetch<{ adminId: string; email: string; role: string }>('GET', `${ADMIN_BASE}/auth/me`);
}

// ============ Dashboard ============

export interface DashboardData {
  totalUsers: number;
  newUsersToday: number;
  activeSubscriptions: number;
  revenueToday: number;
  recentRegistrations: Array<{
    id: string;
    email: string;
    full_name: string | null;
    created_at: string;
  }>;
  healthStatus: Array<{
    service: string;
    status: string;
    checked_at: string;
  }>;
}

export function getDashboard() {
  return adminFetch<DashboardData>('GET', `${ADMIN_BASE}/dashboard`);
}

export function getUsersChart(period = '30') {
  return adminFetch<Array<{ date: string; count: string }>>(
    'GET',
    `${ADMIN_BASE}/dashboard/users-chart?period=${period}`
  );
}

// ============ Users ============

export interface AdminUser {
  id: string;
  email: string;
  is_verified: boolean;
  subscription_status: string;
  created_at: string;
  last_login_at: string | null;
  full_name: string | null;
  birth_date: string | null;
  birth_place: string | null;
}

export interface UsersListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function getUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  subscription?: string;
  verified?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.search) qs.set('search', params.search);
  if (params?.subscription) qs.set('subscription', params.subscription);
  if (params?.verified) qs.set('verified', params.verified);
  if (params?.sortBy) qs.set('sortBy', params.sortBy);
  if (params?.sortOrder) qs.set('sortOrder', params.sortOrder);
  return adminFetch<UsersListResponse>('GET', `${ADMIN_BASE}/users?${qs.toString()}`);
}

export interface UserDetail {
  user: Record<string, unknown>;
  natalChart: Record<string, unknown> | null;
  numerology: Record<string, unknown> | null;
  subscriptions: unknown[];
  payments: unknown[];
}

export function getUserDetail(id: string) {
  return adminFetch<UserDetail>('GET', `${ADMIN_BASE}/users/${id}`);
}

export function blockUser(id: string, blocked: boolean, reason?: string) {
  return adminFetch<void>('PATCH', `${ADMIN_BASE}/users/${id}/block`, { blocked, reason });
}

export function deleteUser(id: string) {
  return adminFetch<void>('DELETE', `${ADMIN_BASE}/users/${id}`);
}

// ============ Calculations ============

export function recalculateUser(userId: string) {
  return adminFetch<void>('POST', `${ADMIN_BASE}/calculations/recalculate/${userId}`);
}

// ============ Stats ============

export interface StatsData {
  totalUsers: number;
  verifiedUsers: number;
  activeSubscriptions: number;
  activeTrials: number;
  expiredTrials: number;
  totalRevenue: number;
  avgRevenue: number;
  conversionRate: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
}

export function getStats() {
  return adminFetch<StatsData>('GET', `${ADMIN_BASE}/stats`);
}

// ============ Broadcast ============

export function postBroadcast(params: {
  text: string;
  channels: 'telegram' | 'email' | 'both';
  subscribers_only: boolean;
}) {
  return adminFetch<{ success: boolean; sent: number; failed: number }>(
    'POST',
    `${ADMIN_BASE}/broadcast`,
    params,
  );
}

// ============ Subscription Management ============

export function postSubscriptionGrant(params: {
  email: string;
  plan: string;
  duration_days: number;
}) {
  return adminFetch<{ success: boolean; end_date: string }>(
    'POST',
    `${ADMIN_BASE}/subscription/grant`,
    params,
  );
}

export function postSubscriptionRevoke(params: { email: string }) {
  return adminFetch<{ success: boolean }>(
    'POST',
    `${ADMIN_BASE}/subscription/revoke`,
    params,
  );
}

// ============ Bulk Operations ============

export interface BulkFilter {
  emailPattern?: string;
  dateFrom?: string;
  dateTo?: string;
  subscriptionStatus?: string;
  lastLoginBefore?: string;
  lastLoginAfter?: string;
  userIds?: string[];
}

export interface BulkResult {
  affected: number;
  failed: number;
  errors: string[];
}

export function bulkBlockUsers(filter: BulkFilter, reason?: string) {
  return adminFetch<{ success: boolean; data: BulkResult }>(
    'POST',
    `${ADMIN_BASE}/users/bulk-block`,
    { filter, reason },
  );
}

export function bulkVerifyUsers(filter: BulkFilter, reason?: string) {
  return adminFetch<{ success: boolean; data: BulkResult }>(
    'POST',
    `${ADMIN_BASE}/users/bulk-verify`,
    { filter, reason },
  );
}

export function bulkDeleteUsers(filter: BulkFilter, reason?: string) {
  return adminFetch<{ success: boolean; data: BulkResult }>(
    'POST',
    `${ADMIN_BASE}/users/bulk-delete`,
    { filter, reason },
  );
}

export function bulkPreview(filter: BulkFilter) {
  return adminFetch<{ success: boolean; data: { count: number } }>(
    'POST',
    `${ADMIN_BASE}/users/bulk-preview`,
    { filter },
  );
}

// ============ Profile Edit ============

export interface ProfileUpdateParams {
  email?: string;
  is_verified?: boolean;
  full_name?: string;
  birth_date?: string;
  birth_time?: string;
  birth_place?: string;
  gender?: string;
  bio?: string;
}

export function updateUserProfile(id: string, params: ProfileUpdateParams) {
  return adminFetch<{ success: boolean; message: string }>(
    'PATCH',
    `${ADMIN_BASE}/users/${id}`,
    params,
  );
}

// ============ Password Reset ============

export function resetUserPassword(id: string) {
  return adminFetch<{
    success: boolean;
    data: {
      temporaryPassword: string;
      expiresAt: string;
      emailSent: boolean;
      warning: string | null;
    };
  }>('POST', `${ADMIN_BASE}/users/${id}/reset-password`);
}

// ============ Trials ============

export function activateTrial(id: string, days: number = 7, reason?: string) {
  return adminFetch<{
    success: boolean;
    data: { userId: string; trialEndDate: string; action: string };
  }>('POST', `${ADMIN_BASE}/users/${id}/trial/activate`, { days, reason });
}

export function extendTrial(id: string, days: number, reason?: string) {
  return adminFetch<{
    success: boolean;
    data: { userId: string; trialEndDate: string; action: string };
  }>('POST', `${ADMIN_BASE}/users/${id}/trial/extend`, { days, reason });
}

export function convertTrial(
  id: string,
  plan: string = 'base',
  durationDays: number = 30,
  reason?: string,
) {
  return adminFetch<{
    success: boolean;
    data: { userId: string; subscriptionId: string; subscriptionEndDate: string };
  }>('POST', `${ADMIN_BASE}/users/${id}/trial/convert`, { plan, duration_days: durationDays, reason });
}

export function getExpiredTrials(page: number = 1, limit: number = 50) {
  return adminFetch<{
    success: boolean;
    data: { trials: Record<string, unknown>[]; total: number; page: number; limit: number };
  }>('GET', `${ADMIN_BASE}/trials/expired?page=${page}&limit=${limit}`);
}

export function getActiveTrials(page: number = 1, limit: number = 50) {
  return adminFetch<{
    success: boolean;
    data: { trials: Record<string, unknown>[]; total: number; page: number; limit: number };
  }>('GET', `${ADMIN_BASE}/trials/active?page=${page}&limit=${limit}`);
}

// ============ Broadcasts ============

export interface Broadcast {
  id: string;
  admin_id: string | null;
  text: string;
  channels: string;
  subscribers_only: boolean;
  sent_count: number;
  failed_count: number;
  created_at: string;
}

export function getBroadcasts(page: number = 1, limit: number = 20, channels?: string) {
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('limit', String(limit));
  if (channels) qs.set('channels', channels);
  return adminFetch<{
    success: boolean;
    data: { broadcasts: Broadcast[]; total: number; page: number; limit: number };
  }>('GET', `${ADMIN_BASE}/broadcasts?${qs.toString()}`);
}

export function getBroadcastDetail(id: string) {
  return adminFetch<{ success: boolean; data: Broadcast }>(
    'GET',
    `${ADMIN_BASE}/broadcasts/${id}`,
  );
}

// ============ Activity Export ============

export function exportActivityCsv(params?: {
  action?: string;
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  limit?: number;
}): Promise<Response> {
  const qs = new URLSearchParams();
  if (params?.action) qs.set('action', params.action);
  if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
  if (params?.dateTo) qs.set('dateTo', params.dateTo);
  if (params?.userId) qs.set('userId', params.userId);
  if (params?.limit) qs.set('limit', String(params.limit));

  const token = localStorage.getItem('admin_token');
  return fetch(`/api/admin/activity/export?${qs.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

// ============ Articles ============

export interface Article {
  id: string;
  title: string;
  slug: string;
  status: string;
  category: string | null;
  views: number;
  created_at: string;
  published_at: string | null;
  deleted_at: string | null;
}

export function getArticles(params?: { page?: number; pageSize?: number; status?: string }) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params?.status) qs.set('status', params.status);
  return adminFetch<{ articles: Article[]; total: number; page: number; pageSize: number; totalPages: number }>(
    'GET',
    `${ADMIN_BASE}/articles?${qs.toString()}`
  );
}

export function getArticle(id: string) {
  return adminFetch<Article>('GET', `${ADMIN_BASE}/articles/${id}`);
}

export function createArticle(data: Record<string, unknown>) {
  return adminFetch<Article>('POST', `${ADMIN_BASE}/articles`, data);
}

export function updateArticle(id: string, data: Record<string, unknown>) {
  return adminFetch<Article>('PATCH', `${ADMIN_BASE}/articles/${id}`, data);
}

export function deleteArticle(id: string) {
  return adminFetch<void>('DELETE', `${ADMIN_BASE}/articles/${id}`);
}

export function publishArticle(id: string) {
  return adminFetch<Article>('POST', `${ADMIN_BASE}/articles/${id}/publish`);
}

export function unpublishArticle(id: string) {
  return adminFetch<Article>('POST', `${ADMIN_BASE}/articles/${id}/unpublish`);
}

// ============ Analytics ============

export function getAnalyticsOverview() {
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/analytics/overview`);
}

export function getAnalyticsRevenue(params?: { period?: string; days?: number }) {
  const qs = new URLSearchParams();
  if (params?.period) qs.set('period', params.period);
  if (params?.days) qs.set('days', String(params.days));
  return adminFetch<Array<Record<string, unknown>>>('GET', `${ADMIN_BASE}/analytics/revenue?${qs.toString()}`);
}

export function getAnalyticsConversion() {
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/analytics/conversion`);
}

export function getAnalyticsRetention() {
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/analytics/retention`);
}

export function getAnalyticsChurn(params?: { period?: string }) {
  const qs = new URLSearchParams();
  if (params?.period) qs.set('period', params.period);
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/analytics/churn?${qs.toString()}`);
}

export function getAnalyticsLtv() {
  return adminFetch<Array<Record<string, unknown>>>('GET', `${ADMIN_BASE}/analytics/ltv`);
}

// ============ Cache ============

export function getCacheStats() {
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/cache/stats`);
}

export function clearCache(scope: string) {
  return adminFetch<Record<string, unknown>>('POST', `${ADMIN_BASE}/cache/clear/${scope}`);
}

// ============ Settings ============

export interface Setting {
  id: string;
  key: string;
  value: string;
  value_type: string;
  description: string | null;
  is_secret: boolean;
}

export function getSettings() {
  return adminFetch<Setting[]>('GET', `${ADMIN_BASE}/settings`);
}

export function updateSettings(data: Record<string, string>) {
  return adminFetch<{ updated: string[]; skipped: string[] }>('PATCH', `${ADMIN_BASE}/settings`, data);
}

// ============ Pricing ============

export interface PricingTier {
  tier: string;
  monthlyPrice: number;
  yearlyPrice: number;
  serviceCount: number;
  coreServiceCount: number;
}

export interface PricingServiceItem {
  id: string;
  name: string;
  description: string | null;
  tier: string;
  price_contribution: string;
  can_disable: boolean;
  is_core: boolean;
  icon: string | null;
}

export function getPricingTiers() {
  return adminFetch<PricingTier[]>('GET', `${ADMIN_BASE}/pricing/tiers`);
}

export function updatePricingTier(id: string, data: Record<string, unknown>) {
  return adminFetch<void>('PATCH', `${ADMIN_BASE}/pricing/tiers/${id}`, data);
}

export function getPricingServices() {
  return adminFetch<PricingServiceItem[]>('GET', `${ADMIN_BASE}/pricing/services`);
}

export function updatePricingService(id: string, data: Record<string, unknown>) {
  return adminFetch<PricingServiceItem>('PATCH', `${ADMIN_BASE}/pricing/services/${id}`, data);
}

// ============ Monitoring ============

export interface ServiceHealth {
  service: string;
  status: 'ok' | 'warning' | 'error' | 'unknown';
  message?: string;
  latency_ms?: number;
  checked_at: string;
}

export interface MonitoringHealthResponse {
  status: string;
  services: ServiceHealth[];
  checked_at: string;
}

export interface MonitoringLogsResponse {
  logs: LogEntry[];
  total: number;
}

export interface LogEntry {
  level: string;
  message: string;
  timestamp: string;
  details?: unknown;
}

export interface MonitoringErrorsResponse {
  errors: ErrorSummary[];
}

export interface ErrorSummary {
  message: string;
  count: number;
  last_occurrence: string;
  first_occurrence: string;
}

export interface MonitoringQueueResponse {
  queues: QueueStatus[];
}

export interface QueueStatus {
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
}

export function getMonitoringHealth() {
  return adminFetch<MonitoringHealthResponse>('GET', `${ADMIN_BASE}/monitoring/health`);
}

export function getMonitoringLogs(params?: { level?: string; limit?: number; search?: string }) {
  const qs = new URLSearchParams();
  if (params?.level) qs.set('level', params.level);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.search) qs.set('search', params.search);
  return adminFetch<MonitoringLogsResponse>('GET', `${ADMIN_BASE}/monitoring/logs?${qs.toString()}`);
}

export function getMonitoringErrors(params?: { days?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.days) qs.set('days', String(params.days));
  if (params?.limit) qs.set('limit', String(params.limit));
  return adminFetch<MonitoringErrorsResponse>('GET', `${ADMIN_BASE}/monitoring/errors?${qs.toString()}`);
}

export function getMonitoringQueue() {
  return adminFetch<MonitoringQueueResponse>('GET', `${ADMIN_BASE}/monitoring/queue`);
}

// ============ Admin Management ============

export function getAdmins() {
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/admins`);
}

export function createAdmin(data: {
  email: string;
  password: string;
  full_name: string;
  role: 'super_admin' | 'admin' | 'moderator' | 'support';
}) {
  return adminFetch<Record<string, unknown>>('POST', `${ADMIN_BASE}/admins`, data);
}

export function updateAdmin(id: string, data: {
  role?: string;
  is_active?: boolean;
  full_name?: string;
}) {
  return adminFetch<Record<string, unknown>>('PATCH', `${ADMIN_BASE}/admins/${id}`, data);
}

export function deleteAdmin(id: string) {
  return adminFetch<Record<string, unknown>>('DELETE', `${ADMIN_BASE}/admins/${id}`);
}

export function getAdminAudit(id: string, params?: { page?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/admins/${id}/audit?${qs.toString()}`);
}

// ============ Backups ============

export function createBackup() {
  return adminFetch<Record<string, unknown>>('POST', `${ADMIN_BASE}/backups/create`);
}

export function getBackups(page = 1, limit = 50) {
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/backups?page=${page}&limit=${limit}`);
}

export function deleteBackup(id: string) {
  return adminFetch<Record<string, unknown>>('POST', `${ADMIN_BASE}/backups/${id}/delete`);
}

export function restoreBackup(id: string) {
  return adminFetch<Record<string, unknown>>('POST', `${ADMIN_BASE}/backups/${id}/restore`);
}

export function downloadBackup(id: string): Promise<Response> {
  const token = localStorage.getItem('admin_token');
  return fetch(`${ADMIN_BASE}/backups/${id}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function getBackupSchedule() {
  return adminFetch<Record<string, unknown>>('GET', `${ADMIN_BASE}/backups/schedule`);
}

export function updateBackupSchedule(id: string, data: {
  cron_expression?: string;
  enabled?: boolean;
  retention_days?: number;
}) {
  return adminFetch<Record<string, unknown>>('PATCH', `${ADMIN_BASE}/backups/schedule/${id}`, data);
}

// ============ Export ============

export function exportCsv(type: 'users' | 'payments' | 'subscriptions' | 'activity', params?: {
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}): Promise<Response> {
  const qs = new URLSearchParams();
  if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
  if (params?.dateTo) qs.set('dateTo', params.dateTo);
  if (params?.limit) qs.set('limit', String(params.limit));

  const token = localStorage.getItem('admin_token');
  return fetch(`/api/admin/export/${type}?${qs.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
